/**
 * Types pour les templates d'étiquettes
 */

export type FieldSource = 'product' | 'lot' | 'computed' | 'freetext';

export type FieldType =
  | 'text'
  | 'number'
  | 'currency'
  | 'date'
  | 'boolean'
  | 'badge'
  | 'qr'
  | 'barcode';

export type FontWeight = 'normal' | 'bold';
export type TextAlign = 'left' | 'center' | 'right';
export type BarcodeType = 'EAN13' | 'CODE128' | 'CODE39';

// Polices Zebra natives (ZPL ^A command)
export type ZebraFont =
  | '0'   // Police Zebra standard (la plus utilisée)
  | 'A'   // U.L.D. Fonts 9×5
  | 'B'   // U.L.D. Fonts 11×7
  | 'D'   // OCR-B 10 cpi
  | 'E'   // OCR-A
  | 'F'   // Gothic 725 BT Bold
  | 'G'   // Gothic 725 BT
  | 'H'   // Swiss 721 BT Bold
  | 'P'   // Swiss 721 BT Roman;

export enum LabelTemplateType {
  SHELF = 'SHELF',
  MEDIUM = 'MEDIUM',
  BULK = 'BULK',
  PROMO = 'PROMO',
  PREORDER = 'PREORDER',
  CUSTOM = 'CUSTOM',
}

export interface FieldConfig {
  id: string;
  source: FieldSource;
  field?: string;
  enabled: boolean;
  label?: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  fontSize?: number;
  fontWeight?: FontWeight;
  fontFamily?: ZebraFont;
  align?: TextAlign;
  format?: string;
  prefix?: string;
  suffix?: string;
  type?: FieldType;
  qrSize?: number;
  barcodeType?: BarcodeType;
  barcodeHeight?: number;
  showBarcodeText?: boolean;
  zIndex: number;
  // Pour les champs de texte libre
  freeText?: string;
}

export interface LabelTemplate {
  id: string;
  tenant_id: string;
  name: string;
  type: LabelTemplateType;
  description?: string;
  width_mm: number;
  height_mm: number;
  dpi: number;
  darkness: number;
  print_speed: number;
  fields_config: FieldConfig[];
  is_default: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface CreateLabelTemplateDto {
  name: string;
  type: LabelTemplateType;
  description?: string;
  width_mm: number;
  height_mm: number;
  dpi?: number;
  darkness?: number;
  print_speed?: number;
  fields_config?: FieldConfig[];
  is_default?: boolean;
  active?: boolean;
}

export interface UpdateLabelTemplateDto {
  name?: string;
  type?: LabelTemplateType;
  description?: string;
  width_mm?: number;
  height_mm?: number;
  dpi?: number;
  darkness?: number;
  print_speed?: number;
  fields_config?: FieldConfig[];
  is_default?: boolean;
  active?: boolean;
}

export interface PrintLabelDto {
  productId: string;
  lotId?: string;
  templateId: string;
  quantity?: number;
}

export interface PrintBatchLabelsDto {
  labels: PrintLabelDto[];
}

export interface PrintLabelResponse {
  success: boolean;
  message: string;
  zpl?: string;
  results?: Array<{
    success: boolean;
    message: string;
  }>;
}

export interface PreviewLabelResponse {
  zpl: string;
  preview_url: string;
}

// Catalogue des champs disponibles
export interface AvailableField {
  id: string;
  field?: string;
  label: string;
  type: FieldType;
  source?: FieldSource;
}

export const PRODUCT_FIELDS: AvailableField[] = [
  { id: 'product_name', field: 'name', label: 'Nom du produit', type: 'text', source: 'product' },
  { id: 'product_code', field: 'code', label: 'Code produit', type: 'text', source: 'product' },
  { id: 'product_barcode', field: 'barcode', label: 'Code-barres EAN', type: 'barcode', source: 'product' },
  { id: 'product_description', field: 'description', label: 'Description', type: 'text', source: 'product' },
  { id: 'product_category', field: 'category.name', label: 'Catégorie', type: 'text', source: 'product' },
  { id: 'product_origin_country', field: 'origin_country', label: 'Origine', type: 'text', source: 'product' },
  { id: 'product_bio', field: 'bio', label: 'Bio', type: 'badge', source: 'product' },
  { id: 'product_vegan', field: 'vegan', label: 'Vegan', type: 'badge', source: 'product' },
];

export const LOT_FIELDS: AvailableField[] = [
  { id: 'lot_number', field: 'lot_number', label: 'N° de lot', type: 'text', source: 'lot' },
  { id: 'lot_supplier_number', field: 'supplier_lot_number', label: 'N° lot fournisseur', type: 'text', source: 'lot' },
  { id: 'lot_reception_date', field: 'reception_date', label: 'Date réception', type: 'date', source: 'lot' },
  { id: 'lot_expiry_date', field: 'expiry_date', label: 'DLC', type: 'date', source: 'lot' },
  { id: 'lot_unit', field: 'unit', label: 'Unité', type: 'text', source: 'lot' },
  { id: 'lot_is_weighted', field: 'is_weighted', label: 'Au poids', type: 'boolean', source: 'lot' },
  { id: 'lot_tva_rate', field: 'tva_rate', label: 'TVA (%)', type: 'number', source: 'lot' },
  { id: 'lot_supplier', field: 'supplier_name', label: 'Fournisseur', type: 'text', source: 'lot' },
  { id: 'lot_producer', field: 'producer_name', label: 'Producteur', type: 'text', source: 'lot' },
  { id: 'lot_organic_certifier', field: 'organic_certifier', label: 'Certif. Bio', type: 'text', source: 'lot' },
];

// Champs de prix (séparés pour meilleure visibilité)
export const PRICE_FIELDS: AvailableField[] = [
  { id: 'product_price', label: 'Prix de vente', type: 'currency', source: 'computed' },
  { id: 'price_per_kg', label: 'Prix au kg', type: 'currency', source: 'computed' },
  { id: 'price_per_unit', label: 'Prix à la pièce', type: 'currency', source: 'computed' },
  { id: 'lot_unit_price', field: 'unit_price_ttc_cents', label: 'Prix de vente (courant)', type: 'currency', source: 'lot' },
  { id: 'lot_purchase_price', field: 'purchase_price_cents', label: 'Prix achat (lot)', type: 'currency', source: 'lot' },
  { id: 'lot_deposit', field: 'deposit_cents', label: 'Consigne', type: 'currency', source: 'lot' },
];

export const COMPUTED_FIELDS: AvailableField[] = [
  { id: 'qr_code_product', label: 'QR Code produit', type: 'qr', source: 'computed' },
  { id: 'qr_code_lot', label: 'QR Code lot', type: 'qr', source: 'computed' },
  { id: 'days_until_expiry', label: 'Jours avant DLC', type: 'number', source: 'computed' },
  { id: 'full_origin', label: 'Origine complète', type: 'text', source: 'computed' },
];

export const FREETEXT_FIELDS: AvailableField[] = [
  { id: 'freetext', label: '✏️ Texte libre', type: 'text', source: 'freetext' },
];

export const ALL_AVAILABLE_FIELDS: AvailableField[] = [
  ...FREETEXT_FIELDS,
  ...PRODUCT_FIELDS,
  ...LOT_FIELDS,
  ...PRICE_FIELDS,
  ...COMPUTED_FIELDS,
];

// === Types pour la détection de changements de prix ===

export interface PriceChangeProduct {
  id: string;
  name: string;
  code: string;
  barcode: string;
  category_name: string | null;
  current_price_cents: number | null;
  last_printed_price_cents: number | null;
  last_printed_at: string | null;
  price_diff_cents: number;
  price_diff_percent: number;
  /** `up_to_date` n'apparaît que sur la tournée d'un rayon entier. */
  status: 'never_printed' | 'price_changed' | 'up_to_date';
  /** Stock lu sur les lots : rayon seul, et rayon + réserve. */
  stock_display?: number;
  stock_total?: number;
  default_template_id: string | null;
}

export interface PriceChangesResponse {
  products: PriceChangeProduct[];
  summary: {
    total_never_printed: number;
    total_price_changed: number;
    total: number;
  };
}
